/* eslint-disable no-undef */
import { useState } from 'react';
import { CHAT_CONFIG } from '../constants/chatConfig';
import { useOpenAI } from './useOpenAI';
import { sendViaMultiChannel } from '../utils/multiChannelUtils';

export const useConversation = () => {
  const [messages, setMessages] = useState([CHAT_CONFIG.INITIAL_MESSAGE]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationContext, setConversationContext] = useState([]);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    location: '',
    details: ''
  });
  const [chatEnded, setChatEnded] = useState(false);
  const [sendingLead, setSendingLead] = useState(false); // 👈 NEW STATE

  const { callOpenAI, extractUserInfo } = useOpenAI();

  const sendMessage = async () => {
    if (!inputMessage.trim() || chatEnded) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    
    const newContext = [
      ...conversationContext,
      { role: 'user', content: inputMessage }
    ];
    setConversationContext(newContext);
    
    setInputMessage('');
    setIsTyping(true);

    try {
      const aiResponseText = await callOpenAI(inputMessage, conversationContext, userInfo);
      
      const aiResponse = {
        id: Date.now() + 1,
        text: aiResponseText,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiResponse]);
      
      const updatedContext = [
        ...newContext,
        { role: 'assistant', content: aiResponseText }
      ];
      setConversationContext(updatedContext);

      const extractedInfo = await extractUserInfo(updatedContext);
      if (Object.keys(extractedInfo).length > 0) {
        setUserInfo(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(extractedInfo).filter(([key, value]) => value && value.trim() !== '')
          )
        }));
      }

      // Check if conversation seems complete
      if (aiResponseText.toLowerCase().includes('angebot') || 
          aiResponseText.toLowerCase().includes('melde mich') ||
          aiResponseText.toLowerCase().includes('kontaktiere')) {
        
        setTimeout(() => {
          checkAndSendLead();
        }, 3000);
      }

    } catch (error) {
      console.error('Message sending error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "Entschuldigung, es gab einen technischen Fehler. Bitte versuche es erneut.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const checkAndSendLead = () => {
    // Check if we have minimal required information
    if (userInfo.projectType && (userInfo.email || userInfo.phone || userInfo.name)) {
      handleSendLead(userInfo); // 👈 RENAMED FUNCTION
    }
  };

const handleSendLead = async (leadData) => {
  setSendingLead(true);
  
  try {
    const result = await sendViaMultiChannel(leadData, messages);
    
    console.log('📊 Multi-channel results:', result);

    // Generate success message based on results
    const channels = [];
    if (result.summary?.email) channels.push('📧 Email');
    if (result.summary?.telegram) channels.push('📱 Telegram');
    if (result.summary?.whatsapp) channels.push('💬 WhatsApp');

    const channelText = channels.length > 0 ? channels.join(', ') : 'Email';

    // Auto-open WhatsApp URL if available
    if (result.summary?.whatsappURL) {
      setTimeout(() => {
        window.open(result.summary.whatsappURL, '_blank');
      }, 1000);
    }

    const completionMessage = {
      id: Date.now() + 999,
      text: `Vielen Dank für das Gespräch! 🎉\n\n✅ Deine Anfrage wurde erfolgreich gesendet via:\n${channelText}\n\nIvo wird sich schnellstmöglich bei Dir melden!`,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, completionMessage]);
    setChatEnded(true);

  } catch (error) {
    console.error('❌ Multi-channel send failed:', error);
    
    const errorMessage = {
      id: Date.now() + 999,
      text: `Es gab einen technischen Fehler beim Senden. 😔\n\nBitte kontaktiere uns direkt:\n📧 ivo@arnet.photography\n📱 +41 79 XXX XX XX\n\nEntschuldige die Unannehmlichkeiten!`,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, errorMessage]);
  } finally {
    setSendingLead(false);
  }
};

  return {
    messages,
    inputMessage,
    setInputMessage,
    isTyping,
    chatEnded,
    sendingLead, // 👈 NEW RETURN VALUE
    userInfo,
    sendMessage
  };
};
